import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { toPng } from "html-to-image";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LOGO, fmtDate } from "@/lib/helpers";
import { cn } from "@/lib/utils";
import { Download, Printer, Droplet, Phone, Mail, CalendarDays, UserCog, Pencil, Check, X, Camera } from "lucide-react";
import { toast } from "sonner";

const initialsOf = (name = "") =>
  name.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "VN";

const Editable = ({ editing, value, onChange, variant = "dark", className, placeholder, type = "text" }) => {
  if (!editing) {
    if (type === "date") return <span className={className}>{value ? fmtDate(value) : "—"}</span>;
    return <span className={className}>{value || placeholder || "—"}</span>;
  }
  return (
    <input
      type={type}
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "w-full bg-transparent border-b outline-none focus:border-gold px-0.5 py-0.5",
        variant === "light" ? "text-white border-white/50 placeholder-white/40" : "text-navy border-navy/40 placeholder-slate-400",
        className
      )}
    />
  );
};

const DetailRow = ({ icon: Icon, label, field, emp, editing, setDraft, type = "text", placeholder }) => (
  <div className="flex items-start gap-2">
    <div className="mt-0.5 rounded-md bg-navy/5 p-1.5"><Icon className="h-3.5 w-3.5 text-gold-dark" /></div>
    <div className="min-w-0 flex-1">
      <p className="text-[8px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>
      <Editable
        editing={editing}
        type={type}
        value={emp[field]}
        onChange={(v) => setDraft((d) => ({ ...d, [field]: v }))}
        placeholder={placeholder}
        className="text-[11px] font-semibold text-navy break-words leading-tight"
      />
    </div>
  </div>
);

export const IDCard = ({ employee, showActions = true }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["founder", "admin"].includes(user?.role);

  const [emp, setEmp] = useState(employee);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(employee);
  const [uploading, setUploading] = useState(false);
  const [qr, setQr] = useState("");
  const ref = useRef(null);
  const photoRef = useRef(null);

  useEffect(() => { setEmp(employee); setDraft(employee); }, [employee]);

  const src = editing ? draft : emp;

  useEffect(() => {
    const payload = `VNS VINAYAKA GROUP OF ENTERPRISES\nName: ${src.name}\nID: ${src.employee_id}\nEmail: ${src.email}\nDept: ${src.department}`;
    QRCode.toDataURL(payload, { margin: 1, width: 240, color: { dark: "#0B2545", light: "#ffffff" } }).then(setQr).catch(() => {});
  }, [src.name, src.employee_id, src.email, src.department]);

  const save = useMutation({
    mutationFn: async () => {
      let id = emp.employee_id;
      const newId = (draft.employee_id || "").trim();
      if (newId && newId !== id) {
        await api.post(`/employees/${id}/change-id`, { new_id: newId });
        id = newId;
      }
      const payload = { ...draft, employee_id: id };
      return (await api.put(`/employees/${id}`, payload)).data;
    },
    onSuccess: (data) => {
      setEmp(data); setDraft(data); setEditing(false);
      qc.invalidateQueries();
      toast.success("ID card updated");
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not save changes"),
  });

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post(`/employees/${emp.employee_id}/photo`, fd);
      setDraft((d) => ({ ...d, photo: r.data.photo }));
      if (!editing) { setEmp((e) => ({ ...e, photo: r.data.photo })); qc.invalidateQueries({ queryKey: ["employees"] }); }
      toast.success("Photo updated");
    } catch { toast.error("Photo upload failed"); }
    setUploading(false);
  };

  const capture = async () => toPng(ref.current, { pixelRatio: 3, cacheBust: true, skipFonts: true, backgroundColor: "#ffffff" });
  const download = async () => {
    const url = await capture();
    const a = document.createElement("a");
    a.href = url; a.download = `VNS_ID_${emp.employee_id}_${(emp.name || "").replace(/\s+/g, "_")}.png`; a.click();
  };
  const print = async () => {
    const url = await capture();
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>VNS ID · ${emp.employee_id}</title><style>@page{margin:12mm}body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh}img{max-width:760px;width:100%}</style></head><body><img src="${url}" onload="window.focus();window.print();window.close();"/></body></html>`);
    w.document.close();
  };

  const startEdit = () => { setDraft(emp); setEditing(true); };
  const cancelEdit = () => { setDraft(emp); setEditing(false); };
  const designation = src.designation || "Data Entry Operator";

  return (
    <div className="inline-block" data-testid={`idcard-${emp.employee_id}`}>
      <div ref={ref} className="flex flex-wrap justify-center gap-5 bg-transparent p-1">
        {/* ---------- FRONT ---------- */}
        <div className="relative w-[320px] h-[508px] rounded-2xl overflow-hidden bg-navy text-white shadow-2xl flex flex-col">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: `url(${LOGO})`, backgroundSize: "220px", backgroundPosition: "center", backgroundRepeat: "no-repeat" }} />
          <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gold/20" />
          <div className="absolute -left-14 top-40 h-28 w-28 rounded-full bg-gold/10" />

          <div className="relative bg-gold px-4 pt-5 pb-7 text-center rounded-b-[40px]">
            <img src={LOGO} alt="VNS" className="mx-auto h-12 w-12 object-contain bg-white rounded-xl p-1 shadow" />
            <div className="mt-2 font-heading text-lg font-extrabold tracking-tight text-navy-900 leading-none">VNS VINAYAKA</div>
            <div className="mt-1 text-[8px] font-semibold uppercase tracking-[0.3em] text-navy/70">Group of Enterprises</div>
          </div>

          <div className="relative -mt-8 flex justify-center">
            <div className="relative h-24 w-24 rounded-full ring-4 ring-gold bg-navy-900 overflow-hidden flex items-center justify-center shadow-lg">
              {src.photo ? <img src={src.photo} alt={src.name} className="h-full w-full object-cover" /> : <span className="font-heading text-2xl font-extrabold text-gold">{initialsOf(src.name)}</span>}
              {editing && (
                <button type="button" onClick={() => photoRef.current?.click()} data-testid={`edit-photo-${emp.employee_id}`} className="absolute inset-0 flex items-center justify-center bg-navy-900/60 text-gold">
                  <Camera className="h-6 w-6" />
                </button>
              )}
            </div>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          </div>

          <div className="relative px-5 mt-3 text-center">
            <Editable editing={editing} variant="light" value={src.name} onChange={(v) => setDraft((d) => ({ ...d, name: v }))} placeholder="Full name" className="font-heading text-xl font-extrabold tracking-tight text-gold leading-tight text-center" />
            <div className="mt-0.5">
              <Editable editing={editing} variant="light" value={designation} onChange={(v) => setDraft((d) => ({ ...d, designation: v }))} placeholder="Designation" className="text-[11px] font-medium uppercase tracking-widest text-white/80 text-center" />
            </div>
          </div>

          <div className="relative mx-6 mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-white/10 px-3 py-2">
              <p className="text-[8px] uppercase tracking-widest text-gold/80">Employee ID</p>
              <Editable editing={editing} variant="light" value={src.employee_id} onChange={(v) => setDraft((d) => ({ ...d, employee_id: v }))} placeholder="ID" className="text-sm font-bold text-white" />
            </div>
            <div className="rounded-lg bg-white/10 px-3 py-2">
              <p className="text-[8px] uppercase tracking-widest text-gold/80">Department</p>
              <Editable editing={editing} variant="light" value={src.department} onChange={(v) => setDraft((d) => ({ ...d, department: v }))} placeholder="Dept" className="text-sm font-bold text-white" />
            </div>
          </div>

          <div className="relative mt-auto flex items-center gap-3 px-5 py-4 bg-navy-900/50 border-t border-white/10">
            {qr ? <img src={qr} alt="QR" className="h-16 w-16 rounded-md bg-white p-1" /> : <div className="h-16 w-16 rounded-md bg-white/20" />}
            <div>
              <p className="text-[10px] font-semibold text-white leading-tight">Scan to verify</p>
              <p className="text-[9px] text-white/60 leading-tight mt-0.5">Data Entry Division</p>
              <p className="text-[9px] text-gold/80 leading-tight mt-1">www.vnvvdesk.in</p>
            </div>
          </div>
        </div>

        {/* ---------- BACK ---------- */}
        <div className="relative w-[320px] h-[508px] rounded-2xl overflow-hidden bg-white text-navy shadow-2xl flex flex-col border border-gold/40">
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `url(${LOGO})`, backgroundSize: "260px", backgroundPosition: "center", backgroundRepeat: "no-repeat" }} />
          <div className="relative bg-navy px-5 py-3 text-center">
            <p className="font-heading text-base font-bold tracking-[0.15em] text-gold">EMPLOYEE DETAILS</p>
          </div>

          <div className="relative flex-1 px-6 py-5 grid grid-cols-1 gap-3.5">
            <DetailRow icon={Mail} label="Email" field="email" emp={src} editing={editing} setDraft={setDraft} placeholder="email@company.com" />
            <DetailRow icon={Phone} label="Phone" field="phone" emp={src} editing={editing} setDraft={setDraft} placeholder="+91…" />
            <DetailRow icon={Droplet} label="Blood Group" field="blood_group" emp={src} editing={editing} setDraft={setDraft} placeholder="O+" />
            <DetailRow icon={Phone} label="Emergency Contact" field="emergency_contact" emp={src} editing={editing} setDraft={setDraft} placeholder="+91…" />
            <DetailRow icon={CalendarDays} label="Date of Joining" field="joining_date" emp={src} editing={editing} setDraft={setDraft} type="date" />
            <DetailRow icon={UserCog} label="Reporting Head" field="reporting_head" emp={src} editing={editing} setDraft={setDraft} placeholder="Head name" />
          </div>

          <div className="relative px-6 pb-2">
            <div className="relative flex items-end justify-between">
              <img src="/vns-signature.png" alt="Authorised signature" className="h-11 object-contain object-left -mb-1" crossOrigin="anonymous" />
              <img src="/vns-stamp.png" alt="Company stamp" className="h-16 w-16 object-contain opacity-90 -rotate-[10deg] -mb-1" crossOrigin="anonymous" />
            </div>
            <div className="mt-1 border-t border-dashed border-navy/30 pt-1">
              <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">Authorised Signatory</p>
            </div>
          </div>

          <div className="relative bg-gold/15 px-5 py-3">
            <p className="text-[8.5px] leading-snug text-navy/70 text-center">
              This card is the property of <span className="font-semibold">VNS Vinayaka Group of Enterprises</span>. If found, please return to the HR Department.
            </p>
          </div>
        </div>
      </div>

      {showActions && (
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          {editing ? (
            <>
              <Button onClick={() => save.mutate()} disabled={save.isPending || uploading} data-testid={`save-idcard-${emp.employee_id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg">
                <Check className="mr-2 h-4 w-4" /> {save.isPending ? "Saving…" : "Save Changes"}
              </Button>
              <Button onClick={cancelEdit} variant="outline" data-testid={`cancel-idcard-${emp.employee_id}`} className="rounded-lg">
                <X className="mr-2 h-4 w-4" /> Cancel
              </Button>
            </>
          ) : (
            <>
              {canEdit && (
                <Button onClick={startEdit} data-testid={`edit-idcard-${emp.employee_id}`} className="bg-gold hover:bg-gold-dark text-navy-900 rounded-lg">
                  <Pencil className="mr-2 h-4 w-4" /> Edit Card
                </Button>
              )}
              <Button onClick={download} data-testid={`download-idcard-${emp.employee_id}`} className="bg-navy hover:bg-navy-900 text-white rounded-lg">
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button onClick={print} variant="outline" data-testid={`print-idcard-${emp.employee_id}`} className="rounded-lg border-navy/20 text-navy hover:bg-navy hover:text-white">
                <Printer className="mr-2 h-4 w-4" /> Print
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
